import React, { useState, useEffect } from 'react';
import { 
  Bot, 
  Users, 
  Calendar, 
  Briefcase, 
  ClipboardList,
  RefreshCw,
  Settings
} from 'lucide-react';
import { supabase } from '../integrations/supabase/client';
import ConfiguracaoIA from './ConfiguracaoIA';
import VagasRH from './VagasRH';
import CandidatosRH from './CandidatosRH';
import AgendaEntrevistas from './AgendaEntrevistas';
import GoogleCalendarIntegration from './GoogleCalendarIntegration';

type RHTab = 'dashboard' | 'configuracao-ia' | 'vagas' | 'candidatos' | 'agenda' | 'integracao';

const RHDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<RHTab>('dashboard');
  const [stats, setStats] = useState({
    vagasAtivas: 0,
    totalCandidatos: 0,
    entrevistasAgendadas: 0
  });
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    carregarStats();
  }, []);

  // Recarregar stats sempre que voltar para o dashboard
  useEffect(() => {
    if (activeTab === 'dashboard') {
      carregarStats();
    }
  }, [activeTab]);


  const carregarStats = async () => {
    setLoadingStats(true);
    try {
      // Carregar vagas ativas
      const { data: vagas, error: vagasError } = await supabase
        .from('vagas_rh')
        .select('id', { count: 'exact' })
        .eq('status', 'ativa');

      // Carregar total de candidatos
      const { data: candidatos, error: candidatosError } = await supabase
        .from('candidatos_rh')
        .select('id', { count: 'exact' });

      // Carregar entrevistas da semana
      const hoje = new Date();
      const proximaSemana = new Date(hoje.getTime() + 7 * 24 * 60 * 60 * 1000);
      
      const { data: entrevistas, error: entrevistasError } = await supabase
        .from('entrevistas_rh')
        .select('id', { count: 'exact' })
        .gte('data_entrevista', hoje.toISOString())
        .lte('data_entrevista', proximaSemana.toISOString());

      if (!vagasError && !candidatosError && !entrevistasError) {
        setStats({
          vagasAtivas: vagas?.length || 0,
          totalCandidatos: candidatos?.length || 0,
          entrevistasAgendadas: entrevistas?.length || 0
        });
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoadingStats(false);
    }
  };


  const StatCard = ({ title, value, icon: Icon, color, description }: {
    title: string;
    value: string | number;
    icon: any;
    color: string;
    description: string;
  }) => (
    <div className="bg-white rounded-xl shadow-lg p-3 sm:p-6 hover:shadow-xl transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500 mt-1">{description}</p>
        </div>
        <div className={`p-2 sm:p-3 rounded-full ${color}`}>
          <Icon className="w-4 sm:w-6 h-4 sm:h-6 text-white" />
        </div>
      </div>
    </div>
  );

  const DashboardContent = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-6">
        <div className="flex flex-col gap-3 sm:gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">
              RH - Recursos Humanos 👥
            </h1>
            <p className="text-gray-600 text-sm sm:text-base lg:text-lg">
              Gerencie vagas, candidatos e processos seletivos
            </p>
          </div>
        <div className="flex justify-end">
          <button
            onClick={carregarStats}
            disabled={loadingStats}
            className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 text-sm"
            title="Atualizar dados"
          >
            <RefreshCw className={`h-3 sm:h-4 w-3 sm:w-4 mr-1 sm:mr-2 ${loadingStats ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{loadingStats ? 'Atualizando...' : 'Atualizar'}</span>
            <span className="sm:hidden">🔄</span>
          </button>
          </div>
        </div>
      </div>

      {/* Stats Cards responsivos */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
        <StatCard
          title="Vagas Ativas"
          value={stats.vagasAtivas}
          icon={Briefcase}
          color="bg-blue-500"
          description="Posições em aberto"
        />
        <StatCard
          title="Candidatos"
          value={stats.totalCandidatos}
          icon={Users}
          color="bg-green-500"
          description="Total cadastrados"
        />
        <StatCard
          title="Entrevistas"
          value={stats.entrevistasAgendadas}
          icon={Calendar}
          color="bg-purple-500"
          description="Agendadas esta semana"
        />
      </div>

      {/* Quick Actions responsivas */}
      <div className="bg-white rounded-xl shadow-lg p-3 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
          Ações Rápidas
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
          <button
            onClick={() => setActiveTab('vagas')}
            className="flex flex-col items-center p-2 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Briefcase className="w-5 sm:w-8 h-5 sm:h-8 text-blue-500 mb-1 sm:mb-2" />
            <span className="text-xs sm:text-sm font-medium text-gray-700">Nova Vaga</span>
          </button>
          
          <button
            onClick={() => setActiveTab('candidatos')}
            className="flex flex-col items-center p-2 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Users className="w-5 sm:w-8 h-5 sm:h-8 text-green-500 mb-1 sm:mb-2" />
            <span className="text-xs sm:text-sm font-medium text-gray-700">Candidatos</span>
          </button>

          <button
            onClick={() => setActiveTab('agenda')}
            className="flex flex-col items-center p-2 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Calendar className="w-5 sm:w-8 h-5 sm:h-8 text-purple-500 mb-1 sm:mb-2" />
            <span className="text-xs sm:text-sm font-medium text-gray-700">Agenda</span>
          </button>

          <button
            onClick={() => setActiveTab('configuracao-ia')}
            className="flex flex-col items-center p-2 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Bot className="w-5 sm:w-8 h-5 sm:h-8 text-orange-500 mb-1 sm:mb-2" />
            <span className="text-xs sm:text-sm font-medium text-gray-700">Config. IA</span>
          </button>

          <button
            onClick={() => setActiveTab('integracao')}
            className="flex flex-col items-center p-2 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Settings className="w-5 sm:w-8 h-5 sm:h-8 text-gray-500 mb-1 sm:mb-2" />
            <span className="text-xs sm:text-sm font-medium text-gray-700">Integrações</span>
          </button>
        </div>
      </div>

    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardContent />;
      case 'configuracao-ia':
        return <ConfiguracaoIA />;
      case 'vagas':
        return <VagasRH />;
      case 'candidatos':
        return <CandidatosRH />;
      case 'agenda':
        return <AgendaEntrevistas />;
      case 'integracao':
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Integrações</h1>
              <p className="text-gray-600">Configure integrações externas para automatizar processos</p>
            </div>
            <GoogleCalendarIntegration />
          </div>
        );
      default:
        return <DashboardContent />;
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Navigation Tabs responsivas */}
      <div className="bg-white rounded-lg shadow-sm p-1 overflow-x-auto">
        <nav className="flex space-x-1 min-w-max">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center whitespace-nowrap ${
              activeTab === 'dashboard'
                ? 'bg-orange-100 text-orange-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <ClipboardList className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
            Dashboard
          </button>
          
          <button
            onClick={() => setActiveTab('vagas')}
            className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center whitespace-nowrap ${
              activeTab === 'vagas'
                ? 'bg-orange-100 text-orange-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Briefcase className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
            Vagas
          </button>

          <button
            onClick={() => setActiveTab('candidatos')}
            className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center whitespace-nowrap ${
              activeTab === 'candidatos'
                ? 'bg-orange-100 text-orange-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Users className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
            Candidatos
          </button>

          <button
            onClick={() => setActiveTab('agenda')}
            className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center whitespace-nowrap ${
              activeTab === 'agenda'
                ? 'bg-orange-100 text-orange-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Calendar className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
            Agenda
          </button>

          <button
            onClick={() => setActiveTab('configuracao-ia')}
            className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center whitespace-nowrap ${
              activeTab === 'configuracao-ia'
                ? 'bg-orange-100 text-orange-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Bot className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
            Config. IA
          </button>

          <button
            onClick={() => setActiveTab('integracao')}
            className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center whitespace-nowrap ${
              activeTab === 'integracao'
                ? 'bg-orange-100 text-orange-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Settings className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
            Integrações
          </button>
        </nav>
      </div>

      {/* Content */}
      {renderContent()}
    </div>
  );
};

export default RHDashboard;